import { ApiError } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useState } from "react";
import type { BillingProfile } from "./billingApi";
import { useBillingProfile, useSaveBillingProfile } from "./queries";

// A short, market-relevant country list (the backend stores a free country *name*, defaulting to
// "France"). We render localized names so the value matches that convention, and always keep any
// existing stored value selectable so switching this field to a dropdown never drops saved data.
const COUNTRY_CODES = [
  "FR",
  "BE",
  "CH",
  "LU",
  "MC",
  "DE",
  "ES",
  "IT",
  "PT",
  "NL",
  "GB",
  "IE",
  "AT",
];

function countryOptions(uiLocale: string, current: string): string[] {
  let names: string[];
  try {
    const dn = new Intl.DisplayNames([uiLocale], { type: "region" });
    names = COUNTRY_CODES.map((c) => dn.of(c) ?? c);
  } catch {
    names = [...COUNTRY_CODES];
  }
  if (current && !names.includes(current)) names = [current, ...names];
  return Array.from(new Set(names));
}

/**
 * The account billing-identity form (billing §C.3): legal name, address, and numéro de TVA
 * intracommunautaire. This is what the invoice buyer block + the Stripe customer are stamped with, so a
 * French restaurateur can produce a compliant B2B facture. Account-root only (gated by the parent).
 */
export function BillingProfileCard() {
  const { t, i18n } = useTranslation();
  const profile = useBillingProfile();
  const save = useSaveBillingProfile();
  const [form, setForm] = useState<BillingProfile | null>(null);

  // Seed the editable form once the profile loads (and re-seed if the server copy changes) — adjusted
  // during render (comparing against the last-seen profile.data reference) rather than via an effect.
  const [lastProfileData, setLastProfileData] = useState(profile.data);
  if (profile.data !== lastProfileData) {
    setLastProfileData(profile.data);
    if (profile.data) setForm(profile.data);
  }

  if (profile.isLoading || !form) {
    return <p className={css({ color: "fg.muted", fontSize: "sm" })}>{t("billing.loading")}</p>;
  }

  const set = (key: keyof BillingProfile, value: string) => setForm({ ...form, [key]: value });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate(form);
  };

  return (
    <section
      className={css({
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: "md",
        padding: "4",
        marginBottom: "5",
        maxW: "2xl",
      })}
    >
      <h2 className={css({ fontWeight: "semibold", marginBottom: "1" })}>
        {t("billing.profile.heading")}
      </h2>
      <p className={css({ color: "fg.muted", fontSize: "sm", marginBottom: "3" })}>
        {t("billing.profile.hint")}
      </p>

      {save.isError && (
        <p
          role="alert"
          data-testid="billing-profile-error"
          className={css({ color: "fg.error", marginBottom: "2", fontSize: "sm" })}
        >
          {save.error instanceof ApiError ? save.error.message : t("billing.profile.saveFailed")}
        </p>
      )}
      {save.isSuccess && (
        <output
          data-testid="billing-profile-saved"
          className={css({
            display: "block",
            color: "green.text",
            marginBottom: "2",
            fontSize: "sm",
          })}
        >
          {t("billing.profile.saved")}
        </output>
      )}

      <form onSubmit={submit} className={css({ display: "grid", gap: "3" })}>
        <Field label={t("billing.profile.legalName")}>
          <input
            className={inputCss}
            value={form.legalName}
            required
            onChange={(e) => set("legalName", e.target.value)}
          />
        </Field>
        <Field label={t("billing.profile.vatNumber")}>
          <input
            className={inputCss}
            value={form.vatNumber}
            placeholder="FR40123456789"
            onChange={(e) => set("vatNumber", e.target.value)}
          />
        </Field>
        <Field label={t("billing.profile.addressLine")}>
          <input
            className={inputCss}
            value={form.addressLine}
            onChange={(e) => set("addressLine", e.target.value)}
          />
        </Field>
        <div
          className={css({
            display: "grid",
            gridTemplateColumns: { base: "1fr", sm: "1fr 2fr" },
            gap: "3",
          })}
        >
          <Field label={t("billing.profile.postalCode")}>
            <input
              className={inputCss}
              value={form.postalCode}
              onChange={(e) => set("postalCode", e.target.value)}
            />
          </Field>
          <Field label={t("billing.profile.city")}>
            <input
              className={inputCss}
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
            />
          </Field>
        </div>
        <Field label={t("billing.profile.country")}>
          <select
            className={inputCss}
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
          >
            {countryOptions(i18n.language, form.country).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </Field>
        <div>
          <button
            type="submit"
            disabled={save.isPending}
            className={css({
              bg: "colorPalette.default",
              color: "colorPalette.fg",
              px: "4",
              py: "2",
              borderRadius: "md",
              fontWeight: "semibold",
              fontSize: "sm",
              cursor: "pointer",
              _disabled: { opacity: 0.5, cursor: "not-allowed" },
            })}
          >
            {save.isPending ? t("billing.profile.saving") : t("billing.profile.save")}
          </button>
        </div>
      </form>
    </section>
  );
}

const inputCss = css({
  width: "full",
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "sm",
  px: "2",
  py: "1.5",
  fontSize: "sm",
});

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className={css({ display: "grid", gap: "1", fontSize: "sm" })}>
      <span className={css({ color: "fg.muted" })}>{label}</span>
      {children}
    </label>
  );
}
